import { DocPageHeading } from '@squareup/dex-feature-docs-page';
import { DocPage } from '@squareup/dex-types-shared-docs';
import { Head } from '@squareup/dex-ui';
import { DocsPageContentLayout } from '@squareup/dex-ui-dex-tech-docs-page-layout';
import { Box } from '@squareup/dex-ui-shared-base';
import { MarkdownDocument } from '@squareup/dex-ui-shared-markdown-components';
import { getNextPublicDocsNameOrDefault } from '@squareup/dex-utils-environment';
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';
import React, { FC, useMemo } from 'react';

import { getMdxComponentMap } from './mdx-component-map';
import styles from './mdx-content.module.css';

interface MdxPageMetadata {
  title: string;
  browserTitle?: string;
  slug: string;
  eyebrow?: string;
  summary?: string;
  releaseStatus?: string;
}

interface MdxPageContentProps {
  mdxSource: MDXRemoteSerializeResult;
  metadata: MdxPageMetadata;
}

const MdxPageContent: FC<MdxPageContentProps> = ({ mdxSource, metadata }) => {
  // Memoize so MDXRemote's `components` prop has stable identity. Without
  // this, every parent re-render produces a fresh component map, MDXRemote
  // remounts its subtree, and any local state in adapter components
  // (notably MdxTabs's `currentTabId`) snaps back to its initial value —
  // which makes tab clicks appear to do nothing.
  const components = useMemo(() => getMdxComponentMap(), []);

  // Mirror Contentful's heading chrome: an h1 + eyebrow + optional
  // release-status pill, with a breadcrumb trail derived from the slug
  // (e.g., `get-started/create-account-and-application` →
  // [Get Started] / [current page]). DocPageHeading reuses the same
  // styles as the Contentful render path so the visual matches.
  const breadcrumbItems = useMemo(() => {
    const segments = metadata.slug.split('/');
    if (segments.length < 2) {
      return [];
    }
    // Drop the last segment — that's the current page (rendered by the
    // h1 below); breadcrumbs only include ancestors.
    const ancestors = segments.slice(0, -1);
    return ancestors.map((seg, i) => {
      const href = `/docs/${ancestors.slice(0, i + 1).join('/')}`;
      const title = seg
        .split(/[_-]/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      return { href, title };
    });
  }, [metadata.slug]);

  const heading = useMemo<
    Partial<Pick<DocPage['heading'], 'summary' | 'title' | 'releaseStatus'>>
  >(() => {
    return {
      title: metadata.title,
      ...(metadata.summary ? { summary: metadata.summary } : {}),
      ...(metadata.releaseStatus
        ? {
            releaseStatus:
              metadata.releaseStatus as DocPage['heading']['releaseStatus'],
          }
        : {}),
    };
  }, [metadata.releaseStatus, metadata.summary, metadata.title]);

  // No `rightSidebar` for now — DocsFloatingToc would consume column
  // width but render empty (we don't yet extract heading sections from
  // the compiled MDX). DocsPageContentLayout uses `grid-item-full` when
  // rightSidebar is absent, giving the article + screenshots the same
  // width as Contentful pages without a TOC.
  return (
    <DocsPageContentLayout>
      <>
        <Head
          title={
            metadata.browserTitle ||
            metadata.title ||
            getNextPublicDocsNameOrDefault()
          }
          description={metadata.summary || ''}
          canonicalPath={`/docs/${metadata.slug}`}
        />
        {metadata.eyebrow && (
          <Box margin={{ bottom: '1x' }}>
            <span>{metadata.eyebrow}</span>
          </Box>
        )}
        <DocPageHeading heading={heading} breadcrumbItems={breadcrumbItems} />
        <Box className={styles['content']}>
          {/* MarkdownDocument applies the `.article` styles
              (img max-width: 100%, list-style, paragraph spacing, etc.)
              that the Contentful render path uses. Without this wrap,
              MDX content renders with browser-default <ul>/<img> styling
              — bullets unindented, screenshots full natural size.
              `.content` (mdx-content.module.css) layers on top to make
              markdown-syntax images fill the column by default — most
              docs images are screenshots, and `.article img` only caps
              max width so smaller-than-column images looked too small
              vs Contentful's render. Per-image override:
              `<img src=... width=... />` JSX still wins. */}
          <MarkdownDocument frontmatter={undefined}>
            <MDXRemote {...mdxSource} components={components} />
          </MarkdownDocument>
        </Box>
      </>
    </DocsPageContentLayout>
  );
};

MdxPageContent.displayName = 'MdxPageContent';

export { MdxPageContent };
export type { MdxPageMetadata, MdxPageContentProps };
